/**
 * 固件升级 HID 设备检测（对齐 upgrading.html）
 */

import type { FirmwareTab } from './firmware-types'

/** 正常模式：reportId=49 同时有 input/output */
export function isNormalModeDevice(dev: HIDDevice | null | undefined): boolean {
  if (!dev?.collections) return false
  return dev.collections.some(
    col =>
      (col.inputReports || []).some(r => r.reportId === 49) &&
      (col.outputReports || []).some(r => r.reportId === 49)
  )
}

/** 升级模式：reportId=0 同时有 input/output */
export function isUpgradeModeDevice(dev: HIDDevice | null | undefined): boolean {
  if (!dev?.collections) return false
  return dev.collections.some(
    col =>
      (col.inputReports || []).some(r => r.reportId === 0) &&
      (col.outputReports || []).some(r => r.reportId === 0)
  )
}

export function getConnectionTypeByProductId(productId: number): 'USB' | '2.4G' | '' {
  if (Number(productId) === 89 || productId === 0x0059) return 'USB'
  if (Number(productId) === 67 || productId === 0x0067) return '2.4G'
  return ''
}

/** 查找满足当前升级连接类型要求的 HID 设备。 */
export function findDeviceForConnectionType(
  devices: HIDDevice[],
  requiredType: 'USB' | '2.4G',
  preferredDevice?: HIDDevice | null
): HIDDevice | null {
  const orderedDevices: HIDDevice[] = []
  const pushDevice = (dev?: HIDDevice | null) => {
    if (dev && !orderedDevices.includes(dev)) orderedDevices.push(dev)
  }

  pushDevice(preferredDevice)
  devices.filter(dev => isNormalModeDevice(dev)).forEach(pushDevice)
  devices.forEach(pushDevice)
  return orderedDevices.find(dev => getConnectionTypeByProductId(dev.productId) === requiredType) || null
}

export function resolveConnectionTypeFromDevices(
  devices: HIDDevice[],
  preferredDevice?: HIDDevice | null
): { actualType: 'USB' | '2.4G' | ''; sourceDevice: HIDDevice | null } {
  const orderedDevices: HIDDevice[] = []
  const pushDevice = (dev?: HIDDevice | null) => {
    if (dev && !orderedDevices.includes(dev)) orderedDevices.push(dev)
  }

  devices.filter(dev => isNormalModeDevice(dev)).forEach(pushDevice)
  pushDevice(preferredDevice)
  devices.forEach(pushDevice)

  for (const dev of orderedDevices) {
    const actualType = getConnectionTypeByProductId(dev.productId)
    if (actualType) return { actualType, sourceDevice: dev }
  }

  return { actualType: '', sourceDevice: null }
}

export function getRequiredConnectionTypeByTab(tab: FirmwareTab): 'USB' | '2.4G' {
  return tab === 'dongle' ? '2.4G' : 'USB'
}

/**
 * 只有耳机和 Dongle 会重启并切换到新的升级模式设备；
 * 蓝牙和降噪不重启，始终复用当前正常模式设备。
 */
export function needsDeviceReconnect(tab: FirmwareTab): boolean {
  const noReconnectTabs: FirmwareTab[] = ['earphoneNoiseCancel', 'earphoneBluetooth']
  return !noReconnectTabs.includes(tab)
}

export function shouldUseSbtOta(tab: FirmwareTab): boolean {
  // 降噪和蓝牙使用 SBT OTA；耳机和 Dongle 使用重启后的简化 OTA。
  return tab === 'earphoneNoiseCancel' || tab === 'earphoneBluetooth'
}

/** 四类升级的进入命令：耳机 0x84、Dongle 0x83、降噪 0x85、蓝牙 0x86。 */
export function getEnterUpgradePacketForTab(tab: FirmwareTab): Uint8Array | null {
  const map: Record<FirmwareTab, number[]> = {
    dongle: [0xaa, 0x53, 0x08, 0x83, 0x00, 0x01, 0x01, 0x76],
    earphone: [0xaa, 0x53, 0x08, 0x84, 0x00, 0x01, 0x01, 0x75],
    earphoneNoiseCancel: [0xaa, 0x53, 0x08, 0x85, 0x00, 0x01, 0x01, 0x74],
    earphoneBluetooth: [0xaa, 0x53, 0x08, 0x86, 0x00, 0x01, 0x01, 0x73],
  }
  const p = map[tab]
  return p ? new Uint8Array(p) : null
}

export async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export async function waitForUpgradeDevice(
  timeoutMs: number,
  requiredType?: 'USB' | '2.4G'
): Promise<HIDDevice | null> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (!navigator.hid) return null
    const devices = await navigator.hid.getDevices()
    const upgradeDevices = devices.filter(d => isUpgradeModeDevice(d))
    const found = requiredType
      ? upgradeDevices.find(d => getConnectionTypeByProductId(d.productId) === requiredType)
      : upgradeDevices[0]
    if (found) return found
    // 部分引导程序重启后会暴露不同的 PID；如果只有一个升级模式 HID，
    // 可以将其视为刚刚重启进入升级模式的目标设备。
    if (requiredType && upgradeDevices.length === 1) return upgradeDevices[0]
    await sleep(800)
  }
  return null
}

export async function openDeviceIfNeeded(dev: HIDDevice): Promise<void> {
  if (!dev.opened) await dev.open()
}
